import React, { useState } from 'react';
import { Camera, MapPin, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { authAPI, contentAPI } from '../../services/api';

const ProfileSettings = ({ userSettings, setUserSettings }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [customLogo, setCustomLogo] = useState(null);
  const { updateUserProfile } = useAuth();
  const { toast } = useToast();

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        variant: 'destructive',
        title: 'Invalid file',
        description: 'Please select an image file'
      });
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: 'destructive',
        title: 'File too large',
        description: 'File size must be less than 5MB'
      });
      return;
    }

    setIsUploading(true);
    
    try {
      // Create FormData for profile picture upload
      const formData = new FormData();
      formData.append('file', file);
      
      // Use dedicated profile picture endpoint (no subscription required)
      const response = await authAPI.uploadProfilePicture(formData);
      
      // Get the uploaded file URL from response
      let uploadedUrl = response.data?.file_url || response.data?.url;
      
      if (uploadedUrl) {
        // Prefix with backend URL if it's a relative path
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        if (uploadedUrl.startsWith('/uploads')) {
          uploadedUrl = `${API_URL}${uploadedUrl}`;
        }
        
        // Update local state with the full URL
        setUserSettings(prev => ({
          ...prev,
          profilePicture: uploadedUrl
        }));
        
        toast({
          title: 'Success!',
          description: 'Profile picture updated successfully'
        });
      } else {
        throw new Error('No URL returned from upload');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: error.response?.data?.detail || 'Failed to upload profile picture'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast({
        variant: 'destructive',
        title: 'Invalid file',
        description: 'Please select an image file for your logo'
      });
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast({
        variant: 'destructive',
        title: 'File too large',
        description: 'Logo file size must be less than 2MB'
      });
      return;
    }

    try {
      // Create FormData for upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', 'Custom Logo');
      formData.append('description', 'User custom logo/brand');
      formData.append('visibility', 'public');
      
      // Upload to backend
      const response = await contentAPI.uploadFile(formData);
      
      // Update local preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setCustomLogo(e.target.result);
      };
      reader.readAsDataURL(file);
      
      toast({
        title: 'Success!',
        description: 'Custom logo uploaded successfully'
      });
    } catch (error) {
      console.error('Logo upload error:', error);
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: error.response?.data?.detail || 'Failed to upload logo'
      });
    }
  };

  const handleInputChange = (field, value) => {
    setUserSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    
    try {
      const updateData = {
        display_name: userSettings.displayName,
        bio: userSettings.bio,
        location: userSettings.location
      };
      
      // Include profile picture if it's a URL (not base64)
      if (userSettings.profilePicture && !userSettings.profilePicture.startsWith('data:')) {
        updateData.profile_picture = userSettings.profilePicture;
      }
      
      // If user has temp email and wants to update it
      if (userSettings.email && !userSettings.email.includes('@igotsoul.temp')) {
        updateData.email = userSettings.email;
      }
      
      const result = await updateUserProfile(updateData);
      
      if (result.success) {
        toast({
          title: 'Success!',
          description: 'Profile updated successfully'
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Update failed',
          description: result.error || 'Failed to update profile'
        });
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred'
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Picture */}
      <div className="flex items-center gap-6">
        <div className="relative">
          <Avatar className="w-24 h-24">
            <AvatarImage src={userSettings.profilePicture} alt="Profile" />
            <AvatarFallback className="bg-gray-700 text-white text-xl">
              {userSettings.displayName?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <label className="absolute -bottom-2 -right-2 bg-gradient-to-r from-yellow-400 to-purple-500 rounded-full p-2 cursor-pointer hover:from-yellow-500 hover:to-purple-600 transition-colors">
            <Camera className="w-4 h-4 text-white" />
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </label>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">Profile Picture</h3>
          <p className="text-gray-400 text-sm">JPG, GIF or PNG. Max size of 5MB.</p>
          {isUploading && (
            <p className="text-yellow-400 text-sm mt-1">Uploading...</p>
          )}
        </div>
      </div>

      {/* Custom Logo Upload */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h4 className="text-white font-semibold mb-3">Custom Logo/Brand</h4>
        <div className="flex items-center gap-4">
          {customLogo && (
            <div className="w-16 h-16 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden">
              <img src={customLogo} alt="Custom Logo" className="w-full h-full object-contain" />
            </div>
          )}
          <div className="flex-1">
            <label className="block">
              <Button
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700 cursor-pointer"
                asChild
              >
                <span>
                  <Camera className="w-4 h-4 mr-2" />
                  Upload Custom Logo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </span>
              </Button>
            </label>
            <p className="text-gray-400 text-xs mt-1">For branding your content. Max 2MB.</p>
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="displayName" className="text-white">Display Name</Label>
          <Input
            id="displayName"
            value={userSettings.displayName}
            onChange={(e) => handleInputChange('displayName', e.target.value)}
            className="bg-gray-800 border-gray-600 text-white focus:border-yellow-400"
            placeholder="Your display name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="username" className="text-white">Username/Handle</Label>
          <Input
            id="username"
            value={userSettings.username}
            onChange={(e) => handleInputChange('username', e.target.value)}
            className="bg-gray-800 border-gray-600 text-white focus:border-yellow-400"
            placeholder="@username"
          />
        </div>
      </div>

      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="email" className="text-white">Email Address</Label>
        <Input
          id="email"
          type="email"
          value={userSettings.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          className="bg-gray-800 border-gray-600 text-white focus:border-yellow-400"
          placeholder="your@email.com"
        />
      </div>

      {/* Bio */}
      <div className="space-y-2">
        <Label htmlFor="bio" className="text-white">Bio/About Me</Label>
        <Textarea
          id="bio"
          value={userSettings.bio}
          onChange={(e) => handleInputChange('bio', e.target.value)}
          className="bg-gray-800 border-gray-600 text-white focus:border-yellow-400 min-h-[100px]"
          placeholder="Tell people about yourself and your content..."
          maxLength={500}
        />
        <p className="text-gray-400 text-sm text-right">
          {userSettings.bio?.length || 0}/500 characters
        </p>
      </div>

      {/* Location */}
      <div className="space-y-2">
        <Label htmlFor="location" className="text-white flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          Location (Optional)
        </Label>
        <Input
          id="location"
          value={userSettings.location}
          onChange={(e) => handleInputChange('location', e.target.value)}
          className="bg-gray-800 border-gray-600 text-white focus:border-yellow-400"
          placeholder="City, State/Country"
        />
      </div>

      {/* Profile Stats */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h4 className="text-white font-semibold mb-3">Profile Statistics</h4>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-yellow-400">247</div>
            <div className="text-gray-400 text-sm">Followers</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-purple-400">18</div>
            <div className="text-gray-400 text-sm">Uploads</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-400">1.2K</div>
            <div className="text-gray-400 text-sm">Total Views</div>
          </div>
        </div>
      </div>

      {/* Verification Status */}
      <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/20 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-white font-semibold">Creator Verification</h4>
            <p className="text-gray-400 text-sm">Get verified to unlock premium features</p>
          </div>
          <Button variant="outline" className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white">
            Apply for Verification
          </Button>
        </div>
      </div>

      {/* Save Changes Button */}
      <div className="flex justify-end pt-4 border-t border-gray-700">
        <Button
          onClick={handleSaveProfile}
          disabled={isSaving}
          className="bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 px-8"
        >
          {isSaving ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin">⏳</span>
              Saving...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Save className="w-4 h-4" />
              Save Profile Changes
            </span>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ProfileSettings;
